export const useShareStore = defineStore("share", () => {
  const toast = useToastStore();
  const debug = useDebug();
  const modals = useModalStore();
  const { t } = useI18n();
  const data = ref<{
    blob: Blob;
    filename: string;
    type: string;
    text: string;
    title: string;
  } | null>(null);

  /**
   * There is currently an issue with share especially on iOS and other async operations.
   * The share will not work well if there's significant delay between the user interaction and when the navigation.share api is called.
   * It's especially apparent on slighly older iOS devices (i.e. iOS 15 )
   *
   *
   * The work around for now is when that occurs, prompt the user to interact one more time to share.
   */

  async function shareFile(text: string, filename: string, fileType: string) {
    const blob = new Blob([text], { type: fileType });
    const file = new File([blob], filename, { type: fileType });
    try {
      await navigator.share({ files: [file] });
    } catch (err) {
      debug.log("Share did not work", err);
      if (!(err as Error).message.includes("AbortError")) {
        toast.errorToast(t("SV.MES_BlockedShareRequest"));
      }
    }
  }

  async function fetchUrlPdfAndShare(
    url: string,
    filename: string,
    shareTitle: string,
    shareText: string,
  ) {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], filename, { type: "application/pdf" });
    const data = { files: [file], title: shareTitle, text: shareText };
    try {
      await navigator.share(data);
    } catch (err) {
      debug.log("Share did not work", err);
      if (!(err as string).toString().includes("AbortError")) {
        showShareModal({
          blob: blob,
          filename: filename,
          text: shareText,
          title: shareTitle,
          type: "application/pdf",
        });
      }
    }
  }

  async function shareBlob(
    blob: Blob,
    filename: string,
    fileType: string,
    shareTitle: string,
    shareText: string,
  ) {
    const file = new File([blob], filename, { type: fileType });
    const data = { files: [file], title: shareTitle, text: shareText };
    try {
      await navigator.share(data);
    } catch (err) {
      debug.log("Share did not work", err);
      if (!(err as string).toString().includes("AbortError")) {
        toast.errorToast(t("SV.MES_BlockedShareRequest"));
      }
    }
  }

  function shareTextFile(text: string, filename: string) {
    shareFile(text, filename + ".txt", "text/plain");
  }

  async function share() {
    if (!data.value) {
      return;
    }
    await shareBlob(
      data.value.blob,
      data.value.filename,
      data.value.type,
      data.value.title,
      data.value.text,
    );
  }

  function showShareModal(_data: {
    blob: Blob;
    filename: string;
    type: string;
    text: string;
    title: string;
  }) {
    data.value = _data;
    modals.showModal("ShareModal");
  }

  return {
    shareFile,
    shareBlob,
    shareTextFile,
    fetchUrlPdfAndShare,
    data,
    share,
  };
});
